import { useState } from 'react';
import { Link, NavLink, Route, Routes } from 'react-router-dom';
import { kanal } from '@beholder/shared';
import { MAIL_ARTEN, MAIL_ART_NAMEN, type MailArt, type MailListenEintrag, type MailStatus, type MailEinstellungen } from '@beholder/shared/module/mail';
import type { ModulSeiteProps } from '../../core/modul';
import { api, modulPfad } from '../../core/api';
import { useLive } from '../../core/live';
import { Auswahl, Leer, Laden, Meldung, useDaten } from '../../core/ui';
import { Absender, ArtMarke, PlusAdressen, Verdacht, wann } from './gemeinsam';
import { MailAnsicht } from './Ansicht';
import { Einstellungen } from './Einstellungen';
import './stil.css';

export type MailStatusProjekt = MailStatus & MailEinstellungen;

export function Seite(p: ModulSeiteProps) {
  const { projekt, ich } = p;
  const status = useDaten(() => api.get<MailStatusProjekt>(modulPfad(projekt.id, 'mail', 'status')), [projekt.id]);
  useLive(kanal(projekt.id, 'mail'), () => void status.neuLaden());
  const basis = `/p/${projekt.id}/mail`;

  return (
    <div className="seite">
      <nav className="ml-unterreiter" aria-label="Mail">
        <NavLink end to={basis}>
          Mails
        </NavLink>
        <NavLink to={`${basis}/einstellungen`}>Einstellungen</NavLink>
      </nav>
      <Routes>
        <Route index element={<MailListe {...p} status={status.daten} />} />
        <Route path="einstellungen" element={<Einstellungen {...p} beiAenderung={() => void status.neuLaden()} />} />
        <Route path=":mailId" element={<MailAnsicht {...p} />} />
      </Routes>
    </div>
  );
}

function Hinweis({ status, istAdmin }: { status: MailStatusProjekt | null; istAdmin: boolean }) {
  if (!status) return null;
  const unsortiert = istAdmin && status.unsortiert ? (
    <Meldung art="info">
      {status.unsortiert === 1 ? '1 Mail wartet' : `${status.unsortiert} Mails warten`} im <Link to="/modul/mail">Posteingang „Unsortiert“</Link> auf ein Projekt.
    </Meldung>
  ) : null;
  if (!status.eingerichtet) {
    return (
      <>
        <Meldung art="info">
          Postfach noch nicht eingerichtet.{' '}
          {istAdmin ? (
            <>
              Im <Link to="/modul/mail">Posteingang</Link> lässt es sich mit „Konto hinzufügen“ einrichten – bis dahin lassen sich Mails als
              .eml-Datei einlesen.
            </>
          ) : (
            'Die Admins kümmern sich darum.'
          )}
        </Meldung>
        {unsortiert}
      </>
    );
  }
  return (
    <>
      {status.plusAdressen.length > 0 && (
        <Meldung art="info">
          Mails an <PlusAdressen adressen={status.plusAdressen} /> landen automatisch in diesem Projekt. Beholder liest die Postfächer nur und
          verschickt nie etwas.
        </Meldung>
      )}
      {unsortiert}
    </>
  );
}

function MailListe({ projekt, ich, status }: ModulSeiteProps & { status: MailStatusProjekt | null }) {
  const [alle, setAlle] = useState(false);
  const [art, setArt] = useState<MailArt | ''>('');
  const { daten, fehler, neuLaden } = useDaten(
    () => api.get<MailListenEintrag[]>(modulPfad(projekt.id, 'mail', `mails${alle ? '?alle=1' : ''}`)),
    [projekt.id, alle],
  );
  useLive(kanal(projekt.id, 'mail'), () => void neuLaden());
  const sichtbar = (daten ?? []).filter((m) => !art || m.art === art);

  return (
    <div className="stapel">
      <Hinweis status={status} istAdmin={ich.istAdmin} />
      <div className="reihe">
        <Auswahl value={art} onChange={(e) => setArt(e.target.value as MailArt | '')} style={{ width: 'auto' }} aria-label="Art">
          <option value="">Alle Arten</option>
          {MAIL_ARTEN.map((a) => (
            <option key={a} value={a}>
              {MAIL_ART_NAMEN[a]}
            </option>
          ))}
        </Auswahl>
        <label className="schalter">
          <input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} /> Ausgeblendete zeigen
        </label>
      </div>
      <Meldung>{fehler}</Meldung>
      {!daten && !fehler && <Laden />}
      {daten && sichtbar.length === 0 && (
        <Leer titel={daten.length ? 'Keine Mails dieser Art' : 'Noch keine Mails in diesem Projekt'}>
          {!daten.length &&
            (status?.plusAdressen.length
              ? `Mails an ${status.plusAdressen.join(' oder ')} erscheinen hier.`
              : 'Mit einem Kürzel in den Einstellungen bekommt das Projekt eine eigene Plus-Adresse.')}
        </Leer>
      )}
      <ul className="ml-liste">
        {sichtbar.map((m) => (
          <li key={m.id}>
            <Link to={`/p/${projekt.id}/mail/${m.id}`} className={`ml-eintrag${m.ausgeblendet ? ' ml-ausgeblendet' : ''}`}>
              <div className="ml-eintrag-kopf">
                <strong>{m.betreff}</strong>
                <ArtMarke art={m.art} />
                <Verdacht m={m} />
              </div>
              <div className="klein">
                <Absender m={m} /> · <span className="leise">{wann(m)}</span>
              </div>
              {m.zusammenfassung && <div className="zeilen-2 leise">{m.zusammenfassung}</div>}
              <div className="ml-eintrag-fuss">
                {m.anhaenge > 0 && <span>{m.anhaenge === 1 ? '1 Anhang' : `${m.anhaenge} Anhänge`}</span>}
                {m.links > 0 && (
                  <span>
                    {m.links === 1 ? '1 Link' : `${m.links} Links`}
                    {m.auffaelligeLinks > 0 && `, ${m.auffaelligeLinks} auffällig`}
                  </span>
                )}
                {m.zuordnung === 'ki' && <span>von der KI zugeordnet</span>}
                {m.auswertung === 'ohne-ki' && <span>ohne KI ausgewertet</span>}
                {m.ausgeblendet && <span>ausgeblendet</span>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
